import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

// For Vercel deployment: empty string means same domain (relative URLs)
const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [guilds, setGuilds] = useState([]);
  const sessionExpiredShown = useRef(false);

  // Set axios default headers and session expiry interceptor
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
    
    // Add response interceptor for session expiry detection
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check if it's a 401 error (unauthorized - session expired)
        if (error.response?.status === 401 && token && !sessionExpiredShown.current) {
          sessionExpiredShown.current = true;
          
          // Show session expired notification
          toast.error(
            "Your session has expired. Please log in again to continue.",
            {
              duration: 10000,
              id: "session-expired",
              action: {
                label: "Login",
                onClick: () => {
                  window.location.href = "/";
                }
              }
            }
          );
          
          // Clear auth state
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
          setGuilds([]);
          
          // Reset the flag after a delay
          setTimeout(() => {
            sessionExpiredShown.current = false;
          }, 5000);
        }
        return Promise.reject(error);
      }
    );
    
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [token]);

  // Fetch guilds
  const fetchGuilds = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/guilds`);
      setGuilds(response.data);
    } catch (error) {
      console.error("Failed to fetch guilds:", error);
    }
  }, [token]);

  // Handle Google OAuth callback
  const handleGoogleCallback = useCallback(async (sessionId) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/google/callback`, {
        session_id: sessionId
      });
      const { token: newToken, user: userData } = response.data;
      localStorage.setItem("token", newToken);
      setToken(newToken);
      setUser(userData);
      await fetchGuilds();
      return userData;
    } catch (error) {
      console.error("Google auth failed:", error);
      throw error;
    }
  }, [fetchGuilds]);

  // Check for OAuth callback on mount
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, handle it first
    if (window.location.hash?.includes('session_id=')) {
      setLoading(true);
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const sessionId = params.get('session_id');
      
      if (sessionId) {
        // Clear the hash from URL
        window.history.replaceState(null, '', window.location.pathname);
        
        handleGoogleCallback(sessionId)
          .then(() => {
            setLoading(false);
          })
          .catch((error) => {
            console.error("OAuth callback error:", error);
            setLoading(false);
          });
        return;
      }
    }
    
    // Normal token verification
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/auth/me`);
        setUser(response.data);
        await fetchGuilds();
      } catch (error) {
        console.error("Token verification failed:", error);
        localStorage.removeItem("token");
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token, fetchGuilds, handleGoogleCallback]);

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password,
      remember_me: rememberMe,
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (username, email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      username,
      email,
      password,
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setGuilds([]);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const createGuild = async (name, discordWebhook, emblem = null) => {
    const response = await axios.post(`${API_URL}/api/guilds`, {
      name,
      discord_webhook: discordWebhook || null,
      emblem: emblem || null,
    });
    await fetchGuilds();
    return response.data;
  };

  const joinGuild = async (inviteCode) => {
    const response = await axios.post(`${API_URL}/api/guilds/join`, {
      invite_code: inviteCode,
    });
    await fetchGuilds();
    return response.data;
  };

  const leaveGuild = async (guildId) => {
    await axios.post(`${API_URL}/api/guilds/${guildId}/leave`);
    await fetchGuilds();
  };

  const updateGuild = async (guildId, data) => {
    await axios.put(`${API_URL}/api/guilds/${guildId}`, data);
    await fetchGuilds();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      guilds,
      login, 
      register, 
      logout,
      updateUser,
      setUser,
      setToken,
      createGuild,
      joinGuild,
      leaveGuild,
      updateGuild,
      fetchGuilds
    }}>
      {children}
    </AuthContext.Provider>
  );
};
